import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, User, ArrowLeft } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi } from "../lib/api";
import { resolveLandingAndSeed } from "../lib/landing";

const CallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, error, getAccessTokenSilently } =
    useAuth0();

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState("Processing authentication...");
  const [userProfile, setUserProfile] = useState<any>(null);
  const api = useApi();
  // The effect below re-fires whenever Auth0/router identities change; without
  // this the landing lookup and navigation would run more than once.
  const redirectedRef = useRef(false);

  useEffect(() => {
    const handleAuth0Callback = async () => {
      try {
        // Check for Auth0 errors first
        const authError = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");

        if (authError) {
          console.error(
            "🔴 [AUTH0_CALLBACK] Auth0 error:",
            authError,
            errorDescription
          );
          setStatus("error");

          if (authError === "access_denied") {
            setMessage(
              "You cancelled the login process. Please try again to access your dashboard."
            );
          } else {
            setMessage(
              `Authentication failed: ${errorDescription || authError}`
            );
          }
          return;
        }

        // Wait for Auth0 to finish processing
        if (isLoading) {
          setMessage("Completing authentication...");
          return;
        }

        // Handle authentication failure
        if (error) {
          console.error("🔴 [AUTH0_CALLBACK] Auth0 hook error:", error);
          setStatus("error");
          setMessage(`Authentication error: ${error.message}`);
          return;
        }

        // Handle successful authentication
        if (isAuthenticated && user) {
          if (redirectedRef.current) return;
          redirectedRef.current = true;

          console.log("✅ [AUTH0_CALLBACK] User authenticated:", user.sub);
          setMessage("Authentication successful! Setting up your account...");

          // Store minimal auth state (Auth0 manages the session)
          const authState = {
            isAuthenticated: true,
            userId: user.sub,
            email: user.email,
            name: user.name || user.nickname,
            picture: user.picture,
            lastLogin: new Date().toISOString(),
          };

          // Store in localStorage for offline detection (Auth0 manages the real session)
          localStorage.setItem("deepchat_auth_state", JSON.stringify(authState));

          setUserProfile(user);
          setStatus("success");
          setMessage("Welcome! Setting up your workspace…");

          // Ask the server where this user belongs: their last-used page's
          // comments if they have a connected page, otherwise the accounts page
          // to connect their first one. Awaiting one cheap request here is free
          // — this screen is already a spinner — and strictly better than the
          // old unconditional 1s timer that guessed from stale localStorage.
          const { target, requiresSignup } = await resolveLandingAndSeed(api);

          if (requiresSignup) {
            // Go straight to signup instead of bouncing via ProtectedRoute.
            console.log("🔄 [AUTH0_CALLBACK] Profile incomplete, to signup");
            navigate(`/signup?redirect=${encodeURIComponent(target)}`, {
              replace: true,
            });
            return;
          }

          console.log("✅ [AUTH0_CALLBACK] Redirecting to", target);
          setMessage("Welcome! Redirecting…");
          navigate(target, { replace: true });
        } else if (!isLoading) {
          // Auth0 finished loading but user is not authenticated
          console.error(
            "🔴 [AUTH0_CALLBACK] User not authenticated after Auth0 processing"
          );
          setStatus("error");
          setMessage("Authentication failed. Please try logging in again.");
        }
      } catch (error: any) {
        console.error("🔴 [AUTH0_CALLBACK] Unexpected error:", error);
        setStatus("error");
        setMessage(`An unexpected error occurred: ${error.message}`);
      }
    };

    handleAuth0Callback();
  }, [
    isAuthenticated,
    isLoading,
    user,
    error,
    searchParams,
    navigate,
    getAccessTokenSilently,
  ]);

  const getStatusIcon = () => {
    switch (status) {
      case "loading":
        return <Loader2 className="h-16 w-16 animate-spin text-blue-600" />;
      case "success":
        return <CheckCircle className="h-16 w-16 text-green-600" />;
      case "error":
        return <XCircle className="h-16 w-16 text-red-600" />;
      default:
        return <Loader2 className="h-16 w-16 animate-spin text-blue-600" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "success":
        return "text-green-600";
      case "error":
        return "text-red-600";
      default:
        return "text-blue-600";
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case "loading":
        return "Processing...";
      case "success":
        return "Welcome!";
      case "error":
        return "Authentication Failed";
      default:
        return "Processing...";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-6 w-6 text-blue-600" />
              <span>Authentication</span>
            </CardTitle>
            <CardDescription>Processing your login</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <div className="flex justify-center">{getStatusIcon()}</div>

              <div>
                <h3 className={`text-lg font-semibold ${getStatusColor()}`}>
                  {getStatusTitle()}
                </h3>
                <p className="text-gray-600 mt-2">{message}</p>
              </div>

              {/* Show user info on success */}
              {status === "success" && userProfile && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {userProfile.picture && (
                      <img
                        src={userProfile.picture}
                        alt="Profile"
                        className="w-10 h-10 rounded-full"
                      />
                    )}
                    <div className="text-left">
                      <h4 className="font-medium text-green-800">
                        {userProfile.name || userProfile.nickname}
                      </h4>
                      <p className="text-sm text-green-700">
                        {userProfile.email}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col space-y-2 mt-6">
                {status === "success" && (
                  <Button
                    onClick={() => navigate("/accounts")}
                    className="w-full"
                  >
                    Go to Facebook Pages
                  </Button>
                )}

                {status === "error" && (
                  <Button onClick={() => navigate("/login")} className="w-full">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Login
                  </Button>
                )}

                {status === "loading" && (
                  <Button
                    onClick={() => navigate("/login")}
                    variant="outline"
                    className="w-full"
                  >
                    Cancel
                  </Button>
                )}
              </div>

              {/* Auto-redirect notice */}
              {status === "success" && (
                <p className="text-xs text-gray-500 mt-4">
                  You will be redirected automatically in a few seconds...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default CallbackPage;
